/**
 * AnalyticsPage — Performance-Übersicht v2
 *
 * Struktur:
 *   1. Header + Zeitraum-Auswahl
 *   2. KPI-Topline (4 Karten mit Trend-Pfeilen)
 *   3. Auffälligkeiten & Empfehlungen (regelbasiert, kein Extra-LLM)
 *   4. Kanalvergleich + Top Posts
 *   5. Beste Zeiten
 *   6. KI-Insights [Erweiterte Analytics — Feature Gate]
 *
 * Feature-Gate (Block 9 — Advanced Analytics Gate):
 *   Basis (alle Pläne):
 *     KPI-Topline, TrendBadge, 7/30-Tage, Kanalvergleich, Top Posts,
 *     Beste Zeiten, Handlungshinweise (generateHints)
 *   Advanced (ab Business / Trial):
 *     90-Tage-Ansicht, Low-Performer + Refresh-Vormerken, KI-Insights
 *   Gate-Key:  "advanced_analytics" — ab Starter-Plan
 *   Trial:     allowed = true (alle Advanced-Features freigeschalten)
 *              → isTrial-Flag zeigt Trial-Banner statt Upgrade-Nudge
 *   Backend:   Endpoints sind offen (kein Auth); Vorbereitung für
 *              Middleware-Check in routes.py kommentiert
 *
 * Backend-Endpunkte:
 *   GET /api/analytics/status
 *   GET /api/analytics/kpis?range_days=N
 *   GET /api/analytics/platforms?range_days=N
 *   GET /api/analytics/top-posts?limit=5
 *   GET /api/analytics/best-times
 *   GET /api/analytics/trend?range_days=N   ← neu
 *   GET /api/analytics/ai-insights?range_days=N
 *
 * Evergreen-Verknüpfung (vorbereitet):
 *   Top-Posts mit niedrigem Score können später direkt für Evergreen-
 *   Überarbeitung markiert werden → NavLink zu /evergreen vorbereitet.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { trackEvent } from "../lib/trackEvent";
import { getVariant } from "../lib/ab_utils";
import axios from "axios";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, Eye, Heart, MessageCircle, Share2,
  Clock, Sparkles, RefreshCw, Loader2, Info, AlertCircle,
  Facebook, Instagram, Linkedin, ChevronDown, ArrowUpRight,
  ArrowDownRight, Minus, Zap, Target, CheckCircle, Wifi,
  TrendingDown, BookmarkPlus, BookmarkCheck, Activity,
} from "lucide-react";
import { formatRelativeTime, formatAbsoluteTime } from "../lib/relativeTime";
import { useFeatureGate } from "../hooks/useFeatureGate";
import UpgradeNudge from "../components/UpgradeNudge";
import { ansichtsModell, ANSICHT } from "./analytics/ansichtsModell";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

const fmt = (num) => {
  if (num == null) return "—";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + " M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + " K";
  return num.toString();
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// ── Platform Icon ─────────────────────────────────────────────────────────────

const PlatformIcon = ({ platform, size = 18 }) => {
  switch (platform?.toLowerCase()) {
    case "facebook":       return <Facebook size={size} className="text-blue-600" />;
    case "instagram":      return <Instagram size={size} className="text-pink-500" />;
    case "linkedin":       return <Linkedin size={size} className="text-blue-700" />;
    case "tiktok":         return <span style={{ fontSize: size }} className="font-bold text-stone-800">TT</span>;
    case "google_business":return <span style={{ fontSize: size }} className="font-bold text-green-600">G</span>;
    default:               return <BarChart3 size={size} className="text-stone-400" />;
  }
};

const PLATFORM_LABEL = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  tiktok: "TikTok",
  google_business: "Google Business",
};

// ── Trend-Indikator ───────────────────────────────────────────────────────────

const TrendBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  if (pct === 0) return (
    <span className="flex items-center gap-0.5 text-xs text-stone-400">
      <Minus size={11} /> ±0%
    </span>
  );
  const up = pct > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${up ? "text-green-600" : "text-red-500"}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {up ? "+" : ""}{pct}% vs. Vorperiode
    </span>
  );
};

// ── KPI Card ──────────────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon: Icon, iconBg, iconColor, subtitle, trendPct }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-sm transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className={iconColor} />
      </div>
      <TrendBadge pct={trendPct} />
    </div>
    <p className="text-2xl font-bold text-stone-900 leading-none">{value}</p>
    <p className="text-sm font-medium text-stone-500 mt-1">{title}</p>
    {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
  </div>
);

// ── Auffälligkeiten (regelbasiert, kein LLM) ─────────────────────────────────

function generateHints(kpis, platforms, trend) {
  if (!kpis || kpis.post_count === 0) return [];
  const hints = [];

  const engRate = kpis.total_reach > 100
    ? (kpis.total_engagement / kpis.total_reach * 100)
    : null;

  // Hohe Reichweite, wenig Interaktion
  if (engRate !== null && engRate < 1.5) {
    hints.push({
      type: "warning",
      icon: Target,
      title: "Reichweite hoch, Reaktionen niedrig",
      text: "Viele Sichtungen, aber wenig Interaktionen. Versuche direktere Fragen oder stärkere Calls-to-Action am Ende deiner Posts.",
    });
  }

  // Dominanter Kanal
  if (platforms.length >= 2) {
    const best = platforms[0];
    const second = platforms[1];
    if (best.avg_score > 0 && second.avg_score > 0 && best.avg_score > second.avg_score * 1.8) {
      hints.push({
        type: "info",
        icon: TrendingUp,
        title: `${PLATFORM_LABEL[best.platform] || capitalize(best.platform)} performt deutlich besser`,
        text: `Dieser Kanal erzielt fast doppelt so viel Engagement wie die anderen. Hier stärker posten könnte sich direkt lohnen.`,
      });
    }
  }

  // Rückläufiges Engagement-Trend
  if (trend?.delta?.engagement_pct !== null && trend?.delta?.engagement_pct < -20) {
    hints.push({
      type: "warning",
      icon: TrendingDown,
      title: "Engagement rückläufig",
      text: `Das Engagement ist im Vergleich zur Vorperiode um ${Math.abs(trend.delta.engagement_pct)}% gesunken. Prüfe ob Format, Thema oder Posting-Zeit optimiert werden sollten.`,
    });
  }

  // Niedriger Score
  if (kpis.avg_engagement_score > 0 && kpis.avg_engagement_score < 30) {
    hints.push({
      type: "warning",
      icon: AlertCircle,
      title: "Engagement-Score ausbaufähig",
      text: "Ein Score unter 30 deutet auf Optimierungspotenzial hin. Stärkere Hooks, bessere Bildqualität oder gezieltere Posting-Zeiten helfen oft sofort.",
    });
  }

  // Starke Performance
  if (kpis.avg_engagement_score >= 65) {
    hints.push({
      type: "success",
      icon: CheckCircle,
      title: "Starke Performance",
      text: "Dein Engagement-Score ist überdurchschnittlich gut. Schau dir deine Top-Posts an und identifiziere, was funktioniert — dann öfter davon.",
    });
  }

  return hints.slice(0, 3);
}

const HINT_STYLE = {
  warning: { border: "border-amber-200", bg: "bg-amber-50", iconColor: "text-amber-500", textColor: "text-amber-800", subColor: "text-amber-700" },
  info:    { border: "border-blue-200",  bg: "bg-blue-50",  iconColor: "text-blue-500",  textColor: "text-blue-800",  subColor: "text-blue-700" },
  success: { border: "border-green-200", bg: "bg-green-50", iconColor: "text-green-500", textColor: "text-green-800", subColor: "text-green-700" },
};

const HintCard = ({ hint }) => {
  const s = HINT_STYLE[hint.type] || HINT_STYLE.info;
  const HintIcon = hint.icon || Info;
  return (
    <div className={`rounded-xl border ${s.border} ${s.bg} px-4 py-3 flex items-start gap-3`}>
      <HintIcon size={16} className={`${s.iconColor} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${s.textColor} leading-snug`}>{hint.title}</p>
        <p className={`text-xs ${s.subColor} mt-0.5 leading-relaxed`}>{hint.text}</p>
      </div>
    </div>
  );
};

// ── Kanalvergleich ────────────────────────────────────────────────────────────

const ChannelBar = ({ platform, score, maxScore, postCount, reach, engagement,
                     messbar, gemesseneBeitraege, isTop, isWeak }) => {
  // Ein Kanal ohne eine einzige gemessene Zeile bekommt KEIN Urteil.
  //
  // Fund 22.08.2026: Vorher haette LinkedIn mit 45 Beitraegen das Etikett
  // "Ausbaufaehig" getragen — ein Negativurteil ueber einen Kanal, der schlicht keine
  // Zahlen herausgibt (403). Das ist die schaerfste Form desselben Fehlers, den diese
  // und die vorige Runde behandeln: aus fehlender Messung eine Aussage machen.
  const ohneMessung = messbar === false;
  const pct = (!ohneMessung && maxScore > 0 && score) ? Math.round((score / maxScore) * 100) : 0;
  const label = PLATFORM_LABEL[platform] || capitalize(platform);

  return (
    <div className="py-3 border-b border-stone-100 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <PlatformIcon platform={platform} size={18} />
          <span className="text-sm font-medium text-stone-700">{label}</span>
          {ohneMessung ? (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500"
                  title="Dieser Kanal gibt seine Kennzahlen über die Schnittstelle nicht heraus. Die Beiträge sind veröffentlicht — nur nicht messbar.">
              nicht messbar
            </span>
          ) : (
            <>
              {isTop && (
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                  Stark
                </span>
              )}
              {isWeak && (
                <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  Ausbaufähig
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-stone-400">
          <span>
            {postCount} Posts
            {!ohneMessung && gemesseneBeitraege != null && gemesseneBeitraege < postCount
              ? ` (${gemesseneBeitraege} gemessen)` : ""}
          </span>
          <span className="font-bold text-stone-700">
            {ohneMessung || score == null ? "—" : score.toFixed(1)}
          </span>
        </div>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isTop ? "bg-gradient-to-r from-green-400 to-green-500" :
            isWeak ? "bg-gradient-to-r from-amber-300 to-amber-400" :
            "bg-gradient-to-r from-fox-fire to-orange-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-stone-400">
        <span>Reichweite: {fmt(reach)}</span>
        <span>Engagement: {fmt(engagement)}</span>
      </div>
    </div>
  );
};

// ── Top Post Card ─────────────────────────────────────────────────────────────

// Kanäle mit einem echten Live-Insights-Aufruf (analytics/post/{id}/live-insights,
// Meta App Review Aufgabe 4 „pages_read_engagement", 24.08.2026). Der tägliche
// Sammler (posting_performance) läuft nur einmal um 01:45 — ein Beitrag, der GERADE
// erst veröffentlicht wurde, hätte bis dahin keine Zahlen. Dieser Knopf holt sie
// direkt von der Meta Graph API, ohne auf den nächsten Sammler-Lauf zu warten.
const LIVE_INSIGHTS_PLATFORMS = new Set(["facebook", "instagram"]);

const TopPostRow = ({ post, rank }) => {
  const { t } = useLanguage();
  const [live, setLive] = useState(null);       // { likes, comments, shares, reach, reach_hinweis }
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveError, setLiveError] = useState("");

  const canFetchLive = LIVE_INSIGHTS_PLATFORMS.has(post.platform) && post.post_id;

  const fetchLive = async () => {
    setLoadingLive(true);
    setLiveError("");
    try {
      const res = await axios.get(`${API}/analytics/post/${post.post_id}/live-insights`);
      if (res.data?.success) {
        setLive(res.data);
      } else {
        setLiveError(res.data?.user_message || t("Kennzahlen nicht abrufbar.", "Metrics unavailable."));
      }
    } catch (e) {
      setLiveError(t("Kennzahlen nicht abrufbar.", "Metrics unavailable."));
    } finally {
      setLoadingLive(false);
    }
  };

  const likes = live?.likes ?? post.likes ?? 0;
  const comments = live?.comments ?? post.comments ?? 0;
  const shares = live?.shares ?? post.shares ?? 0;
  const reach = live?.reach;

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-0">
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fox-fire to-orange-400 flex items-center justify-center text-white font-bold text-xs flex-shrink-0 mt-0.5">
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <PlatformIcon platform={post.platform} size={13} />
          <span className="text-xs text-stone-400">{post.date || "—"}</span>
        </div>
        <p className="text-sm text-stone-700 leading-snug line-clamp-2">
          {post.hook || post.theme || t(`Post #${String(post.post_id || rank).slice(-4)}`, `Post #${String(post.post_id || rank).slice(-4)}`)}
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-stone-400 flex-wrap">
          <span className="flex items-center gap-0.5"><Heart size={11} /> {likes}</span>
          <span className="flex items-center gap-0.5"><MessageCircle size={11} /> {comments}</span>
          <span className="flex items-center gap-0.5"><Share2 size={11} /> {shares}</span>
          {live && (
            <span className="flex items-center gap-0.5">
              <Eye size={11} />
              {reach != null ? reach : t("Reichweite n. verfügbar", "Reach n/a")}
            </span>
          )}
          {canFetchLive && (
            <button
              onClick={fetchLive}
              disabled={loadingLive}
              data-testid={`live-insights-refresh-${post.post_id}`}
              className="flex items-center gap-1 text-fox-fire hover:underline disabled:opacity-50"
              title={t("Aktuelle Zahlen direkt von der Plattform holen", "Fetch current numbers directly from the platform")}
            >
              {loadingLive ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              {t("Live-Kennzahlen", "Live metrics")}
            </button>
          )}
        </div>
        {liveError && <p className="text-xs text-red-500 mt-0.5">{liveError}</p>}
        {live?.reach_hinweis && (
          <p className="text-xs text-stone-400 mt-0.5">{live.reach_hinweis}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-base font-bold text-stone-800">{post.engagement_score?.toFixed(0) || 0}</p>
        <p className="text-xs text-stone-400">{t("Score", "Score")}</p>
      </div>
    </div>
  );
};

// ── Low-Performer Row ─────────────────────────────────────────────────────────

const LowPerformerRow = ({ post, isCandidate, onMark }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-start gap-3 py-3 border-b border-stone-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <PlatformIcon platform={post.platform} size={13} />
          <span className="text-xs text-stone-400">{post.date || post.posted_at || "—"}</span>
          <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
            {t(`Score ${post.engagement_score?.toFixed(0) ?? "0"}`, `Score ${post.engagement_score?.toFixed(0) ?? "0"}`)}
          </span>
        </div>
        <p className="text-sm text-stone-700 leading-snug line-clamp-2">
          {post.hook || post.theme || t(`Post #${String(post.post_id || "").slice(-4)}`, `Post #${String(post.post_id || "").slice(-4)}`)}
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-stone-400">
          <span className="flex items-center gap-0.5"><Eye size={11} /> {fmt(post.impressions)}</span>
          <span className="flex items-center gap-0.5"><Heart size={11} /> {post.likes || 0}</span>
        </div>
      </div>
      <button
        onClick={() => onMark(post.post_id, !isCandidate)}
        title={isCandidate ? t("Vormerkung aufheben", "Remove bookmark") : t("Für Evergreen-Refresh vormerken", "Bookmark for evergreen refresh")}
        className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors flex-shrink-0 ${
          isCandidate
            ? "border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
            : "border-stone-200 bg-white text-stone-600 hover:border-amber-300 hover:text-amber-700"
        }`}
      >
        {isCandidate
          ? <><BookmarkCheck size={12} /> {t("Vorgemerkt", "Bookmarked")}</>
          : <><BookmarkPlus size={12} /> {t("Vormerken", "Bookmark")}</>
        }
      </button>
    </div>
  );
};

// ── KI-Insights ───────────────────────────────────────────────────────────────

const InsightsSection = ({ insights, loading, onReload, gate }) => {
  if (!gate.allowed) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="text-base font-semibold text-stone-800 mb-3 flex items-center gap-2">
          <Sparkles size={18} className="text-violet-500" />
          KI-Insights
        </h2>
        <UpgradeNudge
          {...gate}
          featureLabel="KI-Insights & automatische Empfehlungen"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-stone-800 flex items-center gap-2">
          <Sparkles size={18} className="text-violet-500" />
          KI-Insights
        </h2>
        <button
          onClick={onReload}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {loading ? "Analysiere…" : "Neu analysieren"}
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-6 justify-center text-stone-400 text-sm">
          <Loader2 size={18} className="animate-spin" />
          KI analysiert Performance-Daten…
        </div>
      )}

      {!loading && insights?.insights?.length > 0 && (
        <div className="space-y-3">
          {insights.insights.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-xs flex-shrink-0 mt-0.5">
                {i + 1}
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-800">{item.title}</p>
                <p className="text-sm text-stone-600 mt-0.5 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
          {insights.fallback && (
            <p className="text-xs text-stone-400 pt-2 border-t border-stone-100">
              Basis-Analyse — für tiefere KI-Auswertung bitte CLAUDE_API_KEY konfigurieren.
            </p>
          )}
        </div>
      )}

      {!loading && !insights && (
        <div className="text-center py-8 text-stone-400">
          <Sparkles size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Noch keine Insights geladen.</p>
          <button onClick={onReload} className="mt-2 text-xs text-fox-fire hover:underline">
            Jetzt analysieren →
          </button>
        </div>
      )}

      {/* Fund 25.08.2026: insights kann ein gültiges Objekt mit LEEREM insights[]
          sein (KI antwortet mit "[]" oder ohne "-"-Aufzählung) — vorher deckte
          weder der obige noch der !insights-Zweig diesen Fall ab, die Karte blieb
          nach "Neu analysieren" wortlos leer. Für ein ab Starter bezahltes Feature
          sieht das wie ein kaputter Kauf aus, nicht wie "keine Auffälligkeiten". */}
      {!loading && insights && insights.insights?.length === 0 && (
        <div className="text-center py-8 text-stone-400">
          <Sparkles size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine besonderen Auffälligkeiten für diesen Zeitraum.</p>
          <button onClick={onReload} className="mt-2 text-xs text-fox-fire hover:underline">
            Erneut analysieren →
          </button>
        </div>
      )}
    </div>
  );
};

// ── Leer-Zustand ──────────────────────────────────────────────────────────────

// Plan-Status aus dem Backend (status_breakdown) in Klartext. Unbekannte Werte
// werden roh angezeigt statt verschluckt — lieber ehrlich als hübsch.
const PLAN_STATUS_LABEL = {
  draft: "Entwurf",
  scheduled: "Eingeplant",
  approved: "Freigegeben",
  published: "Veröffentlicht",
  retracted: "Zurückgezogen",
  failed: "Fehlgeschlagen",
  ohne_status: "Ohne Status",
};

/**
 * Ladefehler — was die Seite zeigt, wenn sie NICHT nachsehen konnte.
 *
 * Bewusst kein Leerzustand und keine Zahl: Wir wissen an dieser Stelle nicht, ob es
 * Daten gibt. Alles andere wäre geraten — und geraten hat die Seite hier schon einmal
 * (sie erklärte einen 500er zum leeren Konto).
 */
const Ladefehler = ({ onRetry }) => (
  <div className="bg-white rounded-xl border border-amber-200 p-10 text-center max-w-md mx-auto"
       data-testid="analytics-ladefehler">
    <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
      <RefreshCw size={26} className="text-amber-500" />
    </div>
    <h3 className="font-semibold text-stone-700 mb-2">
      Die Auswertung konnte nicht geladen werden.
    </h3>
    <p className="text-sm text-stone-500 leading-relaxed mb-5">
      Das liegt an uns, nicht an deinen Beiträgen — deine Daten sind unverändert.
      Bitte versuche es gleich noch einmal.
    </p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-1.5 px-4 py-2 bg-fox-fire text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
    >
      <RefreshCw size={15} /> Erneut laden
    </button>
  </div>
);

const EmptyState = ({ rangeDays, status }) => {
  const ab04Variant = useRef("A");

  // Nur echte Zahlen aus dem Backend verwenden. Fehlt ein Feld (ältere Antwort,
  // Fehlerfall), bleibt es null → alter Text, keine erfundene Zahl.
  const planned   = Number.isFinite(status?.planned_posts)   ? status.planned_posts   : null;
  const published = Number.isFinite(status?.published_posts) ? status.published_posts : null;
  const plannedNotPublished = planned !== null && published !== null && planned > 0 && published === 0;

  // ANA-04 (16.08.2026): Für den Fall „veröffentlicht, aber keine Kennzahlen" gab es
  // hier keinen eigenen Ast — er fiel in den Sonst-Zweig, der dem Kunden versprach,
  // die Zahlen erschienen „automatisch", sobald er den ersten Beitrag veröffentlicht.
  // Genau das hatte er dann schon getan. Es gibt aber im ganzen Backend keinen
  // automatischen Schreiber auf `posting_performance` (services/analytics.py:12) —
  // die Zahlen kamen also nie, und das Versprechen war falsch.
  // Das Backend liefert den ehrlichen Grund seit jeher mit (analytics/routes.py:107),
  // er wurde hier nur nicht gelesen. Jetzt schon.
  // Zwei verwandte, aber NICHT gleiche Lagen — beide zeigen denselben Ast, aber mit
  // unterschiedlichem Unterton (20.08.2026):
  //   published_no_metrics       — noch nichts abgeholt („noch nicht")
  //   published_not_measurable   — abgeholt, aber der Kanal gibt nichts heraus
  //                                (LinkedIn antwortet auf socialActions mit 403).
  // Ohne den zweiten Wert fiele die neue Lage in den Sonst-Zweig, der dem Kunden
  // erklärt, die Zahlen kämen „sobald du veröffentlichst" — das hat er längst getan.
  const notMeasurable = status?.reason === "published_not_measurable";
  const publishedNoMetrics = status?.reason === "published_no_metrics" || notMeasurable;
  const backendMessage = typeof status?.message === "string" ? status.message : "";

  // Aufschlüsselung nur zeigen, wenn sie mehr sagt als die Gesamtzahl
  const breakdown = status?.status_breakdown && typeof status.status_breakdown === "object"
    ? Object.entries(status.status_breakdown).filter(([, n]) => Number.isFinite(n) && n > 0)
    : [];

  useEffect(() => {
    ab04Variant.current = getVariant("AB-04", { trackFn: trackEvent });
    trackEvent("analytics_empty_state_viewed", {
      ab04_variant: ab04Variant.current,
      planned_posts: planned,
      published_posts: published,
    });
  }, [planned, published]);

  // Bei vorhandenem Plan wäre "Ersten Post erstellen" falsch — die Posts gibt es ja.
  const ctaLabel = plannedNotPublished
    ? "Zum Monatsplan →"
    : ab04Variant.current === "B"
      ? "Ersten Post jetzt erstellen →"
      : "Zum Monatsplan →";

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-10 text-center max-w-md mx-auto">
      <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <BarChart3 size={28} className="text-stone-300" />
      </div>
      {plannedNotPublished ? (
        <>
          <h3 className="font-semibold text-stone-700 mb-2">
            Deine Beiträge sind geplant — aber noch nicht veröffentlicht.
          </h3>
          <p className="text-sm text-stone-500 leading-relaxed mb-5">
            Du hast <strong className="text-stone-700">{planned} geplante {planned === 1 ? "Beitrag" : "Beiträge"}</strong> im
            Monatsplan, aber noch keinen davon veröffentlicht. Reichweite und Interaktionen
            entstehen erst nach der Veröffentlichung — sobald der erste Beitrag draußen ist,
            erscheinen hier die Zahlen.
          </p>
          {breakdown.length > 1 && (
            <div className="flex flex-wrap justify-center gap-1.5 mb-5 -mt-2">
              {breakdown.map(([key, count]) => (
                <span
                  key={key}
                  className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200"
                >
                  {count}× {PLAN_STATUS_LABEL[key] || key}
                </span>
              ))}
            </div>
          )}
        </>
      ) : publishedNoMetrics ? (
        <>
          <h3 className="font-semibold text-stone-700 mb-2">
            {notMeasurable
              ? "Deine Beiträge sind draußen — der Kanal gibt seine Zahlen nicht heraus."
              : "Deine Beiträge sind draußen — Kennzahlen liegen uns noch nicht vor."}
          </h3>
          <p className="text-sm text-stone-500 leading-relaxed mb-3">
            {backendMessage || (
              `${published ?? "Einige"} Beiträge sind veröffentlicht, aber von den Kanälen ` +
              `wurden noch keine Kennzahlen abgeholt.`
            )}
          </p>
          <p className="text-xs text-stone-400 leading-relaxed mb-5">
            {notMeasurable
              ? "Eine 0 stünde hier für „niemand hat reagiert“ — das wäre gelogen. Richtig ist: "
                + "die Plattform gibt uns die Zahlen nicht heraus. Deine Reichweite siehst du "
                + "direkt im Netzwerk; sobald der Kanal freigeschaltet ist, erscheint sie hier."
              : "Reichweite und Interaktionen liefern die Plattformen selbst — und nur für "
                + "Kanäle, die dafür freigeschaltet sind. Solange hier nichts steht, sind noch "
                + "keine Zahlen eingegangen. Wir erfinden an dieser Stelle nichts."}
          </p>
        </>
      ) : (
        <>
          <h3 className="font-semibold text-stone-700 mb-2">Hier siehst du, wie deine Posts performen.</h3>
          <p className="text-sm text-stone-500 leading-relaxed mb-5">
            Sobald du deinen ersten Beitrag veröffentlichst, erscheinen hier Klicks,
            Reichweite und Interaktionen, sofern der Kanal sie an uns übermittelt.
          </p>
        </>
      )}
      <div className="flex flex-col gap-2 items-center">
        <NavLink
          to="/monthly-plan"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-fox-fire text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
          onClick={() => trackEvent("analytics_empty_cta_clicked", {
            ab04_variant: ab04Variant.current,
            planned_posts: planned,
          })}
        >
          <ArrowUpRight size={15} />
          {ctaLabel}
        </NavLink>
        <NavLink to="/kanale" className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-fox-fire transition-colors mt-1">
          <Wifi size={12} />
          Kanal verbinden
        </NavLink>
      </div>
    </div>
  );
};

// ── Aktivitäten (GET /api/analytics/activity) ────────────────────────────────
// Zeigt echte Ereignisse des Workspace aus analytics_events — auch dann, wenn
// noch keine Performance-Daten existieren.

const ActivitySection = ({ activity }) => {
  const events = Array.isArray(activity?.events) ? activity.events : [];
  if (events.length === 0) return null;

  const total = Number.isFinite(activity?.total_events) ? activity.total_events : null;

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="activity-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold text-stone-800 flex items-center gap-2">
          <Activity size={18} className="text-fox-fire" />
          Letzte Aktivitäten
        </h2>
        {total !== null && (
          <span className="text-xs text-stone-400">
            {total} {total === 1 ? "Ereignis" : "Ereignisse"} insgesamt
          </span>
        )}
      </div>
      <p className="text-xs text-stone-400 mb-3">
        Was in deinem Konto wirklich passiert ist — unabhängig von Reichweiten-Zahlen.
      </p>
      <ol className="space-y-0">
        {events.map((ev, i) => {
          const rel = formatRelativeTime(ev.timestamp);
          const abs = formatAbsoluteTime(ev.timestamp);
          return (
            <li
              key={`${ev.event_type || "event"}-${ev.timestamp || i}-${i}`}
              className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-0"
            >
              <div className="w-2 h-2 rounded-full bg-fox-fire/60 flex-shrink-0 mt-1.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-700 leading-snug">
                  {ev.label || ev.event_type || "Unbekanntes Ereignis"}
                </p>
                {ev.description && (
                  <p className="text-xs text-stone-500 mt-0.5">{ev.description}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                {rel && <p className="text-xs text-stone-500">{rel}</p>}
                {abs && <p className="text-xs text-stone-400 mt-0.5">{abs}</p>}
                {!rel && !abs && ev.date && <p className="text-xs text-stone-400">{ev.date}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const AnalyticsPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading]                   = useState(true);
  // Getrennt von `loading` und von `status`: Ein gescheiterter Ladevorgang ist etwas
  // anderes als ein leeres Konto. Vorher fielen beide auf denselben Leerzustand
  // zusammen — und der behauptete „du hast noch nichts veröffentlicht".
  const [ladefehler, setLadefehler]             = useState(false);
  const [insightsLoading, setInsightsLoading]   = useState(false);
  const [rangeDays, setRangeDays]               = useState(30);
  const [kpis, setKpis]                         = useState(null);
  const [platforms, setPlatforms]               = useState([]);
  const [topPosts, setTopPosts]                 = useState([]);
  const [bestTimes, setBestTimes]               = useState({ best_days: [], best_hours: [] });
  const [insights, setInsights]                 = useState(null);
  const [status, setStatus]                     = useState(null);
  const [trend, setTrend]                       = useState(null);
  const [lowPerformers, setLowPerformers]       = useState([]);
  const [lowPerfAvgScore, setLowPerfAvgScore]   = useState(null);
  const [activity, setActivity]                 = useState(null);
  // Set<post_id> — alle aktuell vorgemerkten Refresh-Kandidaten
  const [refreshCandidates, setRefreshCandidates] = useState(() => new Set());

  // Feature Gate: "Erweiterte Analytics" — ab Business
  const analyticsGate = useFeatureGate("advanced_analytics");

  // Prevent double-autoload
  const autoLoadedRef = useRef(false);

  // ── Kerndaten laden ─────────────────────────────────────────────────────
  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setLadefehler(false);
    try {
      const [statusRes, kpisRes, platformsRes, postsRes, timesRes, trendRes, lowPerfRes, candidatesRes, activityRes] = await Promise.all([
        axios.get(`${API}/analytics/status`),
        axios.get(`${API}/analytics/kpis?range_days=${rangeDays}`),
        axios.get(`${API}/analytics/platforms?range_days=${rangeDays}`),
        axios.get(`${API}/analytics/top-posts?limit=5&range_days=${rangeDays}`),
        axios.get(`${API}/analytics/best-times`),
        axios.get(`${API}/analytics/trend?range_days=${rangeDays}`).catch(() => null),
        axios.get(`${API}/analytics/low-performers?range_days=${rangeDays}&limit=5`).catch(() => null),
        axios.get(`${API}/analytics/refresh-candidates`).catch(() => null),
        // Aktivitäten: optional — ältere Backend-Version kennt den Endpunkt nicht
        axios.get(`${API}/analytics/activity?limit=15`).catch(() => null),
      ]);

      setStatus(statusRes.data);
      setKpis(kpisRes.data);
      setPlatforms(platformsRes.data.platforms || []);
      setTopPosts(postsRes.data.posts || []);
      setBestTimes(timesRes.data || { best_days: [], best_hours: [] });
      if (trendRes?.data) setTrend(trendRes.data);
      if (lowPerfRes?.data) {
        setLowPerformers(lowPerfRes.data.posts || []);
        setLowPerfAvgScore(lowPerfRes.data.avg_score ?? null);
      }
      if (candidatesRes?.data) {
        setRefreshCandidates(new Set((candidatesRes.data.candidates || []).map(c => c.post_id)));
      }
      setActivity(activityRes?.data || null);

    } catch {
      // Der Toast verschwindet nach Sekunden — der Zustand muss bleiben, sonst
      // erklärt die Seite den Fehler anschließend zum leeren Konto.
      setLadefehler(true);
      toast.error("Fehler beim Laden der Analytics");
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  // ── KI-Insights laden ───────────────────────────────────────────────────
  const loadInsights = useCallback(async () => {
    if (!analyticsGate.allowed) return;
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/ai-insights?range_days=${rangeDays}`);
      setInsights(res.data);
    } catch {
      toast.error("Fehler beim Laden der KI-Insights");
    } finally {
      setInsightsLoading(false);
    }
  }, [rangeDays, analyticsGate.allowed]);

  // ── Refresh-Kandidaten ──────────────────────────────────────────────────
  const handleRefreshMark = useCallback(async (postId, markAsCandidate) => {
    try {
      if (markAsCandidate) {
        await axios.post(`${API}/analytics/refresh-candidates/${postId}`);
        setRefreshCandidates(prev => new Set([...prev, postId]));
        setLowPerformers(prev => prev.map(p =>
          p.post_id === postId ? { ...p, is_refresh_candidate: true } : p
        ));
        toast.success("Für Refresh vorgemerkt – sichtbar in Evergreen");
      } else {
        await axios.delete(`${API}/analytics/refresh-candidates/${postId}`);
        setRefreshCandidates(prev => { const s = new Set(prev); s.delete(postId); return s; });
        setLowPerformers(prev => prev.map(p =>
          p.post_id === postId ? { ...p, is_refresh_candidate: false } : p
        ));
        toast.success("Vormerkung aufgehoben");
      }
    } catch {
      toast.error("Fehler beim Speichern der Vormerkung");
    }
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  // Bei Range-Wechsel: Insights-Lock zurücksetzen und alte Insights verwerfen
  useEffect(() => {
    autoLoadedRef.current = false;
    setInsights(null);
  }, [rangeDays]);

  // Auto-load insights sobald Daten geladen + Plan erlaubt es
  useEffect(() => {
    if (!loading && status?.has_data && analyticsGate.allowed && !autoLoadedRef.current) {
      autoLoadedRef.current = true;
      loadInsights();
    }
  }, [loading, status, analyticsGate.allowed, loadInsights]);

  // ── Berechnungen ────────────────────────────────────────────────────────
  const maxScore = Math.max(...platforms.map(p => p.avg_score || 0), 1);
  // „Stark" und „Ausbaufähig" dürfen nur unter Kanälen vergeben werden, die überhaupt
  // gemessen wurden — sonst trägt der unmessbare Kanal das Negativurteil, nur weil er
  // in der Sortierung hinten steht.
  const messbarePlattformen = platforms.filter(p => p.messbar !== false);
  const topPlatformIdx = messbarePlattformen.length > 0
    ? platforms.indexOf(messbarePlattformen[0]) : -1;
  const weakPlatformIdx = messbarePlattformen.length >= 3
    ? platforms.indexOf(messbarePlattformen[messbarePlattformen.length - 1]) : -1;

  const engagementRate = kpis?.total_reach > 0
    ? ((kpis.total_engagement / kpis.total_reach) * 100).toFixed(1)
    : null;

  const hints = generateHints(kpis, platforms, trend);
  const hasData = status?.has_data;
  const ansicht = ansichtsModell({ laden: loading, ladefehler, hatDaten: hasData });

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4 sm:px-6" data-testid="analytics-page">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
              <BarChart3 className="text-fox-fire" />
              Performance-Übersicht
            </h1>
            <p className="text-stone-500 mt-1 text-sm">
              {status?.has_data
                ? `${status.performance_entries} Einträge · Letzte ${rangeDays} Tage`
                : "Noch keine Performance-Daten vorhanden"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={rangeDays}
                onChange={(e) => setRangeDays(Number(e.target.value))}
                className="appearance-none bg-white border border-stone-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-stone-700 focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="range-selector"
                aria-label="Zeitraum auswählen"
              >
                <option value={7}>Letzte 7 Tage</option>
                <option value={30}>Letzte 30 Tage</option>
                {analyticsGate.allowed
                  ? <option value={90}>Letzte 90 Tage</option>
                  : <option value={90} disabled>Letzte 90 Tage – ab Starter</option>
                }
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            </div>
            <button
              onClick={loadAnalytics}
              disabled={loading}
              className="p-2 bg-white border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
              data-testid="refresh-btn"
              title="Aktualisieren"
              aria-label="Aktualisieren"
            >
              <RefreshCw size={17} className={`text-stone-600 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* ── Advanced-Analytics-Kontext ─────────────────────────────── */}
        {analyticsGate.isTrial ? (
          /* Trial-Nutzer: positiver Banner — kein Lock, nur Kontext */
          <div className="flex items-center gap-2.5 px-4 py-3 bg-violet-50 border border-violet-200 rounded-xl text-sm">
            <Sparkles size={14} className="text-violet-500 flex-shrink-0" />
            <span className="text-violet-700">
              <strong>Testphase aktiv</strong> – du hast vollständigen Zugriff auf alle
              Erweiterten Analytics. Nach der Testphase bleiben diese mit dem{" "}
              <NavLink
                to="/settings#plan-management"
                className="underline font-medium hover:text-violet-900"
              >
                Starter-Plan
              </NavLink>{" "}
              erhalten.
            </span>
          </div>
        ) : !analyticsGate.allowed ? (
          /* Solo-Plan ohne Trial: sachlicher Hinweis auf Advanced-Funktionen */
          <UpgradeNudge
            {...analyticsGate}
            featureLabel="Erweiterte Analytics (Trends, Low-Performer, KI-Insights)"
          />
        ) : null}

        {ansicht === ANSICHT.LADEN ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 size={28} className="animate-spin text-fox-fire" />
          </div>
        ) : ansicht === ANSICHT.FEHLER ? (
          <Ladefehler onRetry={loadAnalytics} />
        ) : ansicht === ANSICHT.LEER ? (
          <EmptyState rangeDays={rangeDays} status={status} />
        ) : (
          <>
            {/* ── KPI Topline ─────────────────────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
              <KPICard
                title="Reichweite"
                value={fmt(kpis?.total_reach)}
                icon={Eye}
                iconBg="bg-blue-50"
                iconColor="text-blue-500"
                // Eine große 0 behauptete „niemand hat es gesehen". Richtig ist „das
                // erheben wir nicht": Bluesky liefert Likes/Reposts/Antworten, keine
                // Impressionen; LinkedIn gibt gar nichts heraus. `fmt(null)` zeigt „—",
                // und die Unterzeile sagt, warum.
                subtitle={kpis && kpis.reichweite_erhoben === false
                  ? "von den Kanälen nicht erhoben"
                  : "Impressionen gesamt"}
                trendPct={trend?.delta?.reach_pct}
              />
              <KPICard
                title="Engagement"
                value={fmt(kpis?.total_engagement)}
                icon={Heart}
                iconBg="bg-pink-50"
                iconColor="text-pink-500"
                subtitle={kpis ? `${kpis.total_likes || 0} Likes, ${kpis.total_comments || 0} Komm.` : ""}
                trendPct={trend?.delta?.engagement_pct}
              />
              <KPICard
                title="Engagement-Rate"
                value={engagementRate !== null ? `${engagementRate} %` : "—"}
                icon={TrendingUp}
                iconBg="bg-green-50"
                iconColor="text-green-500"
                subtitle="Engagement ÷ Reichweite"
                trendPct={null}
              />
              <KPICard
                title="Posts analysiert"
                value={kpis?.post_count || 0}
                icon={BarChart3}
                iconBg="bg-violet-50"
                iconColor="text-violet-500"
                // Ohne diese Zeile bleibt unerklärt, warum das Engagement neben einer
                // großen Beitragszahl klein aussieht: Die Zahlen darüber stammen NUR
                // aus den messbaren Beiträgen — bei LinkedIn sind das null.
                subtitle={kpis && kpis.nicht_messbare_beitraege > 0
                  ? `${kpis.gemessene_beitraege} mit Zahlen · ${kpis.nicht_messbare_beitraege} ohne`
                  : (kpis?.top_platform
                      // Die Zahl gehoert dazu: "bester Kanal (1 Beitrag)" ist eine
                      // andere Aussage als "bester Kanal (200)".
                      ? `Bester Kanal: ${PLATFORM_LABEL[kpis.top_platform.platform] || kpis.top_platform.platform}`
                        + (kpis.top_platform.post_count
                            ? ` (${kpis.top_platform.post_count} ${kpis.top_platform.post_count === 1 ? "Beitrag" : "Beiträge"})`
                            : "")
                      : "")}
                trendPct={trend?.delta?.posts_pct}
              />
            </div>

            {/* ── Auffälligkeiten ──────────────────────────────────────── */}
            {hints.length > 0 && (
              <div data-testid="hints-section">
                <h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Zap size={14} className="text-fox-fire" />
                  Auffälligkeiten & Empfehlungen
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {hints.map((h, i) => <HintCard key={i} hint={h} />)}
                </div>
              </div>
            )}

            {/* ── Kanalvergleich + Top Posts ───────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Kanalvergleich — 3 Spalten */}
              <div className="lg:col-span-3 bg-white rounded-xl border border-stone-200 p-6" data-testid="channel-comparison">
                <h2 className="text-base font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <BarChart3 size={18} className="text-fox-fire" />
                  Kanalvergleich
                </h2>
                {platforms.length > 0 ? (
                  <div>
                    {platforms.map((p, idx) => (
                      <ChannelBar
                        key={p.platform}
                        platform={p.platform}
                        score={p.avg_score}
                        maxScore={maxScore}
                        postCount={p.post_count}
                        reach={p.reach}
                        engagement={p.engagement}
                        messbar={p.messbar}
                        gemesseneBeitraege={p.gemessene_beitraege}
                        isTop={idx === topPlatformIdx && messbarePlattformen.length >= 2}
                        isWeak={idx === weakPlatformIdx && messbarePlattformen.length >= 3}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-stone-400">
                    <Info size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Noch keine Plattform-Daten</p>
                  </div>
                )}
              </div>

              {/* Top Posts — 2 Spalten */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-stone-200 p-6" data-testid="top-posts">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-stone-800 flex items-center gap-2">
                    <TrendingUp size={18} className="text-amber-500" />
                    {t("Top Posts", "Top Posts")}
                  </h2>
                  {/* Vorbereitung Evergreen-Verknüpfung:
                      Später: Low-Score-Posts direkt hier mit "Für Evergreen markieren" verlinken */}
                  <NavLink
                    to="/evergreen"
                    className="text-xs text-stone-400 hover:text-fox-fire transition-colors"
                    title={t("Evergreen-Inhalte ansehen", "View evergreen content")}
                  >
                    {t("Evergreen →", "Evergreen →")}
                  </NavLink>
                </div>
                {topPosts.length > 0 ? (
                  topPosts.map((post, i) => (
                    <TopPostRow key={post.post_id || i} post={post} rank={i + 1} />
                  ))
                ) : (
                  <div className="text-center py-8 text-stone-400">
                    <Info size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">{t("Noch keine Posts mit Daten", "No posts with data yet")}</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Verbesserungspotenzial / Low-Performer ──────────────── */}
            {analyticsGate.allowed ? (
              /* Advanced — Inhalt zeigen wenn Daten vorhanden */
              lowPerformers.length > 0 && (
                <div className="bg-white rounded-xl border border-amber-200 p-6" data-testid="low-performers">
                  <div className="flex items-start justify-between mb-1">
                    <h2 className="text-base font-semibold text-stone-800 flex items-center gap-2">
                      <TrendingDown size={18} className="text-amber-500" />
                      {t("Verbesserungspotenzial", "Room for improvement")}
                    </h2>
                    {lowPerfAvgScore !== null && (
                      <span className="text-xs text-stone-400 mt-0.5">
                        {t(`Perioden-Ø Score: ${lowPerfAvgScore}`, `Period avg. score: ${lowPerfAvgScore}`)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-400 mb-3">
                    {t(
                      "Posts mit unterdurchschnittlichem Engagement – markiere sie für einen gezielten Refresh.",
                      "Posts with below-average engagement – bookmark them for a targeted refresh.",
                    )}
                  </p>
                  {lowPerformers.map((post) => (
                    <LowPerformerRow
                      key={post.post_id}
                      post={post}
                      isCandidate={refreshCandidates.has(post.post_id)}
                      onMark={handleRefreshMark}
                    />
                  ))}
                  {refreshCandidates.size > 0 && (
                    <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                      <span className="text-xs text-stone-400">
                        {t(
                          `${refreshCandidates.size} ${refreshCandidates.size === 1 ? "Inhalt" : "Inhalte"} vorgemerkt`,
                          `${refreshCandidates.size} ${refreshCandidates.size === 1 ? "item" : "items"} bookmarked`,
                        )}
                      </span>
                      <NavLink
                        to="/evergreen"
                        className="text-xs text-fox-fire hover:underline font-medium"
                      >
                        {t("In Evergreen ansehen →", "View in evergreen →")}
                      </NavLink>
                    </div>
                  )}
                </div>
              )
            ) : (
              /* Basis-Plan — Teaser-Karte erklärt die Funktion ohne Lock-Gefühl */
              <div
                className="bg-stone-50 border border-dashed border-stone-300 rounded-xl p-5"
                data-testid="low-performers-gate"
              >
                <div className="flex items-start gap-3">
                  <TrendingDown size={18} className="text-stone-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-600 mb-0.5">
                      {t("Verbesserungspotenzial", "Room for improvement")}
                    </p>
                    <p className="text-xs text-stone-400 mb-3 leading-relaxed">
                      {t(
                        "Zeigt Posts mit unterdurchschnittlichem Engagement – zum gezielten Überarbeiten und Vormerken für Evergreen-Refresh. Verfügbar ab Starter.",
                        "Shows posts with below-average engagement – for targeted rework and bookmarking for an evergreen refresh. Available from the Starter plan.",
                      )}
                    </p>
                    <UpgradeNudge
                      {...analyticsGate}
                      featureLabel={t("Low-Performer-Analyse & Refresh-Vormerken", "Low-performer analysis & refresh bookmarking")}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Beste Zeiten ─────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="best-times">
              <h2 className="text-base font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Clock size={18} className="text-green-500" />
                Beste Zeiten
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">Beste Wochentage</p>
                  {bestTimes.best_days?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bestTimes.best_days.map((d, i) => (
                        <span key={i} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-100">
                          {d.day}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400">Noch nicht genug Daten (mind. 3 Posts pro Tag)</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">Beste Uhrzeiten</p>
                  {bestTimes.best_hours?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {bestTimes.best_hours.map((h, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-100">
                          {h.hour}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400">Noch nicht genug Daten (mind. 3 Posts pro Zeitblock)</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── KI-Insights ──────────────────────────────────────────── */}
            <InsightsSection
              insights={insights}
              loading={insightsLoading}
              onReload={loadInsights}
              gate={analyticsGate}
            />

            {/* ── Footer ───────────────────────────────────────────────── */}
            <p className="text-center text-xs text-stone-400 pb-4">
              Basiert auf {kpis?.post_count || 0} Posts · Letzte {rangeDays} Tage
              {trend?.previous?.posts > 0 && ` · Vorperiode: ${trend.previous.posts} Posts`}
            </p>
          </>
        )}

        {/* ── Letzte Aktivitäten ─────────────────────────────────────── */}
        {!loading && <ActivitySection activity={activity} />}
      </div>
    </div>
  );
};

export default AnalyticsPage;
